export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;
const RAD = Math.PI / 180;

/** Great-circle distance in km (haversine). */
export function km(a: LatLng, b: LatLng): number {
  const dLat = (b.lat - a.lat) * RAD;
  const dLng = (b.lng - a.lng) * RAD;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

/**
 * Date-line unwrapping: shifts each longitude by ±360 so consecutive points
 * are never more than 180° apart. A route Tokyo → Honolulu then draws across
 * the Pacific instead of across the whole world.
 */
export function unwrapLngs(lngs: readonly number[]): number[] {
  const out: number[] = [];
  let prev: number | null = null;
  for (const lng of lngs) {
    const u: number = prev == null ? lng : lng + 360 * Math.round((prev - lng) / 360);
    out.push(u);
    prev = u;
  }
  return out;
}

/**
 * Longitudes of a set of places moved onto the shortest arc that holds them all: the map is cut at the widest empty
 * gap, so a set spanning Jamaica, Beijing and Rio is framed across the Atlantic, not across the Pacific. Order kept.
 */
export function spanLngs(lngs: readonly number[]): number[] {
  const w = lngs.map((l) => (l >= -180 && l < 180 ? l : ((((l + 180) % 360) + 360) % 360) - 180));
  if (w.length < 2) return w;
  const s = [...w].sort((a, b) => a - b);
  // the gap across the date line first, so a tie keeps the usual Greenwich-centred view
  let gap = s[0]! + 360 - s[s.length - 1]!;
  let start = s[0]!;
  for (let i = 1; i < s.length; i++) {
    const g = s[i]! - s[i - 1]!;
    if (g > gap + 1e-9) {
      gap = g;
      start = s[i]!;
    }
  }
  return w.map((l) => (l < start ? l + 360 : l));
}

/** A hop longer than this (degrees) inside the framed arc goes the short way round instead, off one edge and back in at the other. */
export const LONG_HOP = 230;

/**
 * Longitudes of a route for drawing: each place on the same arc as `spanLngs`, so the line runs across the framed
 * map; only a hop that would cross most of the world (e.g. Yokohama → San Francisco on a trip round the world)
 * takes the short way over the date line.
 */
export function routeLngs(lngs: readonly number[]): number[] {
  const a = spanLngs(lngs);
  const out: number[] = [];
  a.forEach((l, i) => {
    if (i === 0) return out.push(l);
    const prev = out[i - 1]!;
    let d = l - a[i - 1]!;
    if (Math.abs(d) > LONG_HOP) d -= 360 * Math.sign(d);
    out.push(prev + d);
  });
  return out;
}

export function fmtKm(d: number): string {
  if (d < 1) return `${Math.round(d * 1000)} m`;
  if (d < 20) return `${d.toFixed(1)} km`;
  return `${Math.round(d).toLocaleString("en-GB")} km`;
}
